import React from 'react';
import ReactMarkdown from 'react-markdown';
import * as GeminiService from '../services/geminiService';
import { createRedirectUrl } from '../services/redirectService';
import { DishAnalysis, LocationResult, MapChunk } from '../types';

interface AnalysisResultsProps {
  analysis: DishAnalysis;
  restaurantResult: LocationResult | null;
  supermarketResult: LocationResult | null;
  loadingMaps: boolean;
  onGenerateImage: () => void;
  isGeneratingImage: boolean;
  generatedImage: string | null;
}

// --- Partner Logos ---
const UberEatsLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const DoorDashLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M23.23 11.23c-.76-1.54-2.28-2.5-3.97-2.5h-10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5h10.5c.34 0 .6.26.6.6s-.26.6-.6.6h-10.5c-2.48 0-4.5-2.02-4.5-4.5s2.02-4.5 4.5-4.5h10.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5h-10.5c-4.14 0-7.5 3.36-7.5 7.5s3.36 7.5 7.5 7.5h10.5c2.48 0 4.5 2.02 4.5 4.5s-2.02 4.5-4.5 4.5h-10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5h10.5c4.14 0 7.5-3.36 7.5-7.5 0-1.69-.56-3.21-1.5-4.43l-2.03-3.07z"/>
  </svg>
);

const InstacartLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.5 2H4.5A2.5 2.5 0 0 0 2 4.5v15A2.5 2.5 0 0 0 4.5 22h15a2.5 2.5 0 0 0 2.5-2.5v-15A2.5 2.5 0 0 0 19.5 2zm-12.23 4.22c.16-.32.48-.52.83-.52h7.8c.35 0 .67.2.83.52l1.63 3.26c.15.31.14.68-.04.98-.18.3-.51.48-.86.48H4.54c-.35 0-.68-.18-.86-.48-.18-.3-.19-.67-.04-.98l1.63-3.26zM15 18H9a1 1 0 0 1 0-2h6a1 1 0 0 1 0 2z"/>
  </svg>
);

const ShiptLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3.36 15.12L12 15.36l-3.36 1.76.64-3.74-2.72-2.65 3.76-.55L12 6.77l1.68 3.39 3.76.55-2.72 2.65.64 3.74z"/>
  </svg>
);

const PlaceCard: React.FC<{ chunk: MapChunk; type: 'restaurant' | 'supermarket' }> = ({ chunk, type }) => {
  const storeId = chunk.title || '';

  const getOptions = () => {
    if (type === 'restaurant') {
      return [
        { 
          name: 'UberEats', 
          label: 'Uber Eats', 
          url: createRedirectUrl('ubereats', storeId), 
          className: 'bg-[#06C167] text-white hover:bg-[#05a357] shadow-emerald-500/20', 
          icon: <UberEatsLogo /> 
        },
        { 
          name: 'DoorDash', 
          label: 'DoorDash', 
          url: createRedirectUrl('doordash', storeId), 
          className: 'bg-[#FF3008] text-white hover:bg-[#d42605] shadow-red-500/20', 
          icon: <DoorDashLogo /> 
        },
        { 
          name: 'Carryout', 
          label: 'Local Pickup', 
          url: `https://www.google.com/search?q=${encodeURIComponent(storeId)}+order+online+pickup`, 
          className: 'bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          ) 
        },
      ];
    } else {
      return [
        { 
          name: 'Instacart', 
          label: 'Instacart', 
          url: createRedirectUrl('instacart', storeId), 
          className: 'bg-[#FF8300] text-white hover:bg-[#e67600] shadow-orange-500/20', 
          icon: <InstacartLogo /> 
        },
        { 
          name: 'Shipt', 
          label: 'Shipt', 
          url: createRedirectUrl('shipt', storeId), 
          className: 'bg-[#003C52] text-white hover:bg-[#002a3a] shadow-blue-500/20', 
          icon: <ShiptLogo /> 
        },
        { 
          name: 'StoreInfo', 
          label: 'Official Site', 
          url: `https://www.google.com/search?q=${encodeURIComponent(storeId)}+official+website`, 
          className: 'bg-teal-600 text-white hover:bg-teal-700 shadow-teal-500/20', 
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
          ) 
        },
      ];
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-2xl transition-all duration-500 flex flex-col h-full group/card relative overflow-hidden">
      <div className="mb-5 relative z-10">
        <h4 className="font-black text-slate-900 dark:text-white text-xl leading-tight group-hover/card:text-indigo-600 dark:group-hover/card:text-indigo-400 transition-colors">
          {chunk.title}
        </h4>
      </div>
      
      <div className="mt-auto space-y-4 relative z-10">
        <a 
          href={chunk.uri} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="flex items-center justify-center gap-2 w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-sm shadow-lg shadow-indigo-600/30 transition-all duration-300 transform hover:-translate-y-0.5"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Get Directions
        </a>

        <div className="grid grid-cols-1 gap-2.5">
          {getOptions().map((opt) => (
            <a 
              key={opt.name}
              href={opt.url}
              className={`flex items-center justify-between px-4 py-3 rounded-xl text-xs font-black transition-all duration-300 transform hover:-translate-y-0.5 hover:scale-[1.01] active:scale-95 shadow-md hover:shadow-lg ${opt.className}`}
            >
              <div className="flex items-center gap-3">
                 <span className="flex items-center justify-center">{opt.icon}</span>
                 <span>{opt.label}</span>
              </div>
              <svg className="w-3.5 h-3.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

const NutritionCard: React.FC<{ label: string; value: string; colorClass: string }> = ({ label, value, colorClass }) => (
  <div className={`flex flex-col p-4 rounded-2xl bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600 transition-all hover:scale-[1.02]`}>
    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">{label}</span>
    <span className={`text-xl font-black ${colorClass}`}>{value}</span>
  </div>
);

const AnalysisResults: React.FC<AnalysisResultsProps> = ({ 
  analysis, 
  restaurantResult, 
  supermarketResult,
  loadingMaps,
  onGenerateImage,
  isGeneratingImage,
  generatedImage
}) => {
  // Fixed: Added safer mapping and template literal backticks
  const ingredientsList = analysis.ingredients || [];
  const recipeSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(analysis.dishName + ' healthy recipes ' + ingredientsList.map(i => i.name).slice(0, 2).join(' '))}`;
  const googleMapsSearchUrl = `https://www.google.com/maps/search/${encodeURIComponent(analysis.dishName + ' restaurants near me')}`;

  return (
    <div className="space-y-12 animate-fade-in py-4 max-w-7xl mx-auto">
      
      {/* ALERTS */}
      {(analysis.allergenAlerts?.length ?? 0) > 0 && (
        <div className="bg-rose-50 dark:bg-rose-900/20 border-l-8 border-rose-500 p-8 rounded-3xl shadow-xl ring-1 ring-rose-500/10 animate-pulse-subtle">
          <div className="flex items-start">
            <div className="bg-rose-500 p-3 rounded-2xl mr-5 shadow-lg shadow-rose-500/40 text-white">
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-black text-rose-800 dark:text-rose-300">Safety Warning</h3>
              <p className="text-rose-700 dark:text-rose-200 font-bold mb-3 opacity-90">Potential allergens identified in this meal:</p>
              <ul className="space-y-1.5">
                {analysis.allergenAlerts?.map((alert, idx) => (
                  <li key={idx} className="text-rose-900 dark:text-rose-100 font-black flex items-center gap-3 bg-white/50 dark:bg-black/20 px-3 py-1.5 rounded-xl w-fit">
                    <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                    {alert}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* MAIN ANALYSIS CONTENT */}
      <div className="grid lg:grid-cols-2 gap-12">
        <div className="bg-white dark:bg-slate-800 p-10 rounded-[3rem] shadow-2xl border border-slate-100 dark:border-slate-700 transition-all hover:shadow-indigo-500/5">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-8">
             <h2 className="text-5xl font-black text-slate-900 dark:text-white leading-tight tracking-tighter">{analysis.dishName}</h2>
             <div className="flex flex-wrap gap-2">
               {analysis.isHealthy ? (
                 <span className="bg-emerald-500 text-white text-[10px] px-5 py-2 rounded-full font-black uppercase tracking-widest shadow-lg shadow-emerald-500/30">Optimal Health</span>
               ) : (
                 <span className="bg-amber-500 text-white text-[10px] px-5 py-2 rounded-full font-black uppercase tracking-widest shadow-lg shadow-amber-500/30">High Energy</span>
               )}
             </div>
          </div>
          
          <p className="text-2xl text-slate-600 dark:text-slate-300 mb-10 leading-snug font-medium italic opacity-90">
            "{analysis.description}"
          </p>
          
          {/* IMAGE SECTION */}
          <div className="mb-12 group relative">
            {generatedImage ? (
              <div className="rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-white dark:border-slate-700 transform hover:scale-[1.01] transition-all duration-500">
                <img src={generatedImage} alt={analysis.dishName} className="w-full aspect-video object-cover" />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 to-transparent flex justify-center">
                  <span className="text-[10px] text-white/80 font-black uppercase tracking-[0.3em]">AI Photorealistic View</span>
                </div>
              </div>
            ) : (
              <button
                onClick={onGenerateImage}
                disabled={isGeneratingImage}
                className="w-full py-6 px-8 bg-gradient-to-br from-violet-600 via-indigo-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white rounded-[2.5rem] font-black text-xl shadow-2xl shadow-indigo-600/30 transition-all active:scale-[0.98] flex items-center justify-center gap-4 group/btn"
              >
                {isGeneratingImage ? (
                  <>
                    <svg className="animate-spin h-7 w-7 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Cooking up a visualization...
                  </>
                ) : (
                  <>
                    <svg className="w-7 h-7 group-hover/btn:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    See It Plated
                  </>
                )}
              </button>
            )}
          </div>

          <h3 className="text-3xl font-black text-slate-900 dark:text-white mb-8 flex items-center gap-4">
            <span className="w-10 h-10 flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/50 rounded-2xl">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </span>
            Components & Swaps
          </h3>
          <div className="space-y-6">
            {analysis.ingredients?.map((ing, idx) => (
              <div key={idx} className="flex flex-col border-b border-slate-100 dark:border-slate-700 last:border-0 pb-6 last:pb-0">
                <div className="flex justify-between items-center mb-3">
                  <span className="font-black text-slate-900 dark:text-slate-100 text-xl tracking-tight">{ing.name}</span>
                  {ing.benefit && (
                    <span className="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-4 py-1.5 rounded-full font-black uppercase tracking-tighter">
                      {ing.benefit}
                    </span>
                  )}
                </div>
                
                {ing.healthyAlternative && (
                  <div className="group bg-emerald-50 dark:bg-emerald-900/20 p-5 rounded-[2rem] border-2 border-emerald-500/10 hover:border-emerald-500/30 transition-all transform hover:scale-[1.01]">
                    <div className="flex items-start gap-4">
                      <div className="mt-1 bg-emerald-500 rounded-full p-1 text-white shadow-lg shadow-emerald-500/40">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <div className="text-sm">
                        <p className="font-black text-emerald-900 dark:text-emerald-100 text-lg mb-1 leading-tight">Try {ing.healthyAlternative} instead!</p>
                        <p className="text-emerald-700 dark:text-emerald-300 font-medium leading-snug">{ing.reasonForAlternative}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )) || <p className="text-slate-400 font-bold">No ingredient details available.</p>}
          </div>
        </div>

        {/* RIGHT COLUMN: NUMBERS & ADVICE */}
        <div className="space-y-12">
          <div className="bg-white dark:bg-slate-800 p-10 rounded-[3rem] shadow-2xl border border-slate-100 dark:border-slate-700">
            {analysis.nutritionalInfo && (
              <div className="mb-12">
                <h3 className="text-3xl font-black text-slate-900 dark:text-white mb-8 flex items-center gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-orange-100 dark:bg-orange-900/50 rounded-2xl">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </span>
                  Macro Breakdown
                </h3>
                
                <div className="grid grid-cols-2 gap-5 mb-8">
                  <NutritionCard label="Calories" value={analysis.nutritionalInfo.calories} colorClass="text-slate-900 dark:text-white" />
                  <NutritionCard label="Protein" value={analysis.nutritionalInfo.protein} colorClass="text-blue-600 dark:text-blue-400" />
                  <NutritionCard label="Carbs" value={analysis.nutritionalInfo.carbohydrates} colorClass="text-orange-600 dark:text-orange-400" />
                  <NutritionCard label="Fats" value={analysis.nutritionalInfo.fats} colorClass="text-rose-600 dark:text-rose-400" />
                </div>
                
                <div className="flex gap-5">
                  <div className="flex-1 p-5 rounded-[2rem] bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600 group hover:bg-white dark:hover:bg-slate-800 transition-colors duration-300 shadow-sm">
                    <span className="block text-[10px] text-slate-400 dark:text-slate-500 uppercase font-black tracking-widest mb-2">Dietary Fiber</span>
                    <span className="text-2xl font-black text-slate-800 dark:text-white">{analysis.nutritionalInfo.fiber || '0g'}</span>
                  </div>
                  <div className="flex-1 p-5 rounded-[2rem] bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600 group hover:bg-white dark:hover:bg-slate-800 transition-colors duration-300 shadow-sm">
                    <span className="block text-[10px] text-slate-400 dark:text-slate-500 uppercase font-black tracking-widest mb-2">Total Sugar</span>
                    <span className="text-2xl font-black text-slate-800 dark:text-white">{analysis.nutritionalInfo.sugar || '0g'}</span>
                  </div>
                </div>

                {(analysis.nutritionalInfo.vitaminsAndMinerals?.length ?? 0) > 0 && (
                   <div className="mt-10 pt-8 border-t border-slate-100 dark:border-slate-700">
                      <h4 className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-5">Micronutrient Profile</h4>
                      <div className="flex flex-wrap gap-3">
                         {analysis.nutritionalInfo.vitaminsAndMinerals?.map((v, i) => (
                            <span key={i} className="text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-5 py-2 rounded-full font-black border-2 border-indigo-500/10 hover:border-indigo-500/40 transition-all cursor-default">
                              {v}
                            </span>
                         ))}
                      </div>
                   </div>
                )}
              </div>
            )}

            <h3 className="text-3xl font-black text-slate-900 dark:text-white mb-8 flex items-center gap-4">
              <span className="w-10 h-10 flex items-center justify-center bg-emerald-100 dark:bg-emerald-900/50 rounded-2xl">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </span>
              Health Coaching
            </h3>
            <ul className="space-y-6">
              {analysis.healthTips?.map((tip, idx) => (
                <li key={idx} className="flex items-start bg-slate-50 dark:bg-slate-900/20 p-6 rounded-[2rem] border-l-[10px] border-emerald-500 font-bold text-slate-700 dark:text-slate-300 text-lg leading-snug shadow-sm">
                  {tip}
                </li>
              )) || <p className="text-slate-400">Personalizing your tips...</p>}
            </ul>
          </div>
        </div>
      </div>

      {/* LOCATOR SECTION */}
      <div className="space-y-12 mt-20 pb-20">
        <div className="flex flex-col items-center text-center">
          <span className="text-[10px] font-black uppercase tracking-[0.5em] text-indigo-500 mb-4">Hyper-Local Discovery</span>
          <h2 className="text-6xl font-black text-slate-900 dark:text-white tracking-tighter mb-6">Satisfy the Craving</h2>
          <div className="h-2.5 w-32 bg-indigo-600 rounded-full shadow-lg shadow-indigo-600/30"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* RESTAURANTS */}
          <div className="flex flex-col h-full bg-slate-100 dark:bg-slate-900/40 rounded-[4rem] p-6 border border-slate-200 dark:border-slate-800">
             <div className="h-48 bg-gradient-to-br from-rose-400 via-orange-500 to-amber-600 rounded-[3rem] flex flex-col items-center justify-center relative overflow-hidden shadow-2xl mb-10 group">
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-all duration-700"></div>
                <div className="relative z-10 flex flex-col items-center">
                  <span className="text-7xl mb-4 transform group-hover:scale-125 transition-transform duration-700 drop-shadow-2xl">🥗</span>
                  <h3 className="text-4xl font-black text-white drop-shadow-lg uppercase tracking-tight">Ready to Eat</h3>
                </div>
                {loadingMaps && (
                  <div className="absolute top-8 right-10 flex items-center gap-2 bg-white/20 backdrop-blur-xl px-5 py-2.5 rounded-full border border-white/30 text-white font-black text-xs">
                    <span className="w-2.5 h-2.5 bg-white rounded-full animate-ping"></span>
                    Scanning radius...
                  </div>
                )}
             </div>
             
             <div className="px-6 flex-grow flex flex-col pb-6">
               <div className="mb-12 p-10 bg-gradient-to-r from-orange-500 to-rose-600 rounded-[3.5rem] shadow-2xl shadow-orange-500/30 flex flex-col lg:flex-row items-center justify-between gap-8 transform hover:-translate-y-2 transition-transform duration-500 border-t-4 border-orange-400/30">
                  <div className="text-center lg:text-left">
                    <h4 className="font-black text-white text-3xl tracking-tighter mb-2">Dining Out? 🍽️</h4>
                    <p className="text-orange-100 font-bold opacity-80 text-lg">Find the best rated spots for {analysis.dishName}.</p>
                  </div>
                  <a 
                    href={googleMapsSearchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white text-orange-600 px-10 py-5 rounded-[2rem] font-black text-lg shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3"
                  >
                    Find Nearby
                    <svg className="w-5 h-5 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </a>
               </div>

               {loadingMaps ? (
                 <div className="space-y-8 animate-pulse opacity-40">
                   <div className="h-10 bg-slate-300 dark:bg-slate-700 rounded-2xl w-3/4"></div>
                   <div className="h-64 bg-slate-200 dark:bg-slate-800 rounded-[3rem] w-full"></div>
                 </div>
               ) : restaurantResult ? (
                 <>
                  <div className="prose prose-2xl prose-indigo dark:prose-invert max-w-none mb-12 font-bold tracking-tight leading-snug">
                    <ReactMarkdown>{restaurantResult.text}</ReactMarkdown>
                  </div>
                  {(restaurantResult.mapChunks?.length ?? 0) > 0 && (
                      <div className="mt-auto grid grid-cols-1 sm:grid-cols-2 gap-8">
                        {restaurantResult.mapChunks.map((chunk, i) => (
                          <PlaceCard key={i} chunk={chunk} type="restaurant" />
                        ))}
                      </div>
                  )}
                 </>
               ) : (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-400 font-black uppercase text-sm tracking-widest opacity-50">
                    No Vendors Found Nearby
                  </div>
               )}
             </div>
          </div>

          {/* GROCERY & RECIPES */}
          <div className="flex flex-col h-full bg-slate-100 dark:bg-slate-900/40 rounded-[4rem] p-6 border border-slate-200 dark:border-slate-800">
             <div className="h-48 bg-gradient-to-br from-emerald-400 via-teal-500 to-indigo-600 rounded-[3rem] flex flex-col items-center justify-center relative overflow-hidden shadow-2xl mb-10 group">
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-all duration-700"></div>
                <div className="relative z-10 flex flex-col items-center">
                  <span className="text-7xl mb-4 transform group-hover:scale-125 transition-transform duration-700 drop-shadow-2xl">🥦</span>
                  <h3 className="text-4xl font-black text-white drop-shadow-lg uppercase tracking-tight">Stock Up</h3>
                </div>
                {loadingMaps && (
                  <div className="absolute top-8 right-10 flex items-center gap-2 bg-white/20 backdrop-blur-xl px-5 py-2.5 rounded-full border border-white/30 text-white font-black text-xs">
                    <span className="w-2.5 h-2.5 bg-white rounded-full animate-ping"></span>
                    Locating aisles...
                  </div>
                )}
             </div>

             <div className="px-6 flex-grow flex flex-col pb-6">
               <div className="mb-12 p-10 bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-[3.5rem] shadow-2xl shadow-indigo-600/30 flex flex-col lg:flex-row items-center justify-between gap-8 transform hover:-translate-y-2 transition-transform duration-500 border-t-4 border-indigo-400/30">
                  <div className="text-center lg:text-left">
                    <h4 className="font-black text-white text-3xl tracking-tighter mb-2">Be Your Own Chef 🧑‍🍳</h4>
                    <p className="text-indigo-100 font-bold opacity-80 text-lg">Curated healthy recipes for {analysis.dishName}.</p>
                  </div>
                  <a 
                    href={recipeSearchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white text-indigo-700 px-10 py-5 rounded-[2rem] font-black text-lg shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3"
                  >
                    Get Recipes
                    <svg className="w-5 h-5 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  </a>
               </div>

               {loadingMaps ? (
                 <div className="space-y-8 animate-pulse opacity-40">
                   <div className="h-10 bg-slate-300 dark:bg-slate-700 rounded-2xl w-3/4"></div>
                   <div className="h-64 bg-slate-200 dark:bg-slate-800 rounded-[3rem] w-full"></div>
                 </div>
               ) : supermarketResult ? (
                 <>
                   <div className="prose prose-2xl prose-emerald dark:prose-invert max-w-none mb-12 font-bold tracking-tight leading-snug">
                      <ReactMarkdown>{supermarketResult.text}</ReactMarkdown>
                   </div>
                   {(supermarketResult.mapChunks?.length ?? 0) > 0 && (
                     <div className="mt-auto grid grid-cols-1 sm:grid-cols-2 gap-8">
                        {supermarketResult.mapChunks.map((chunk, i) => (
                           <PlaceCard key={i} chunk={chunk} type="supermarket" />
                        ))}
                     </div>
                  )}
                 </>
               ) : (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-400 font-black uppercase text-sm tracking-widest opacity-50">
                    No Grocers Identified
                  </div>
               )}
             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AnalysisResults;